import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { AlertCircle, Save, Scale } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { FormActions, FormField, FormPageHeader, FormSection } from '@/components/common/FormSection'
import { Case, UpdateCaseDto } from '../types/case'
import { caseService } from '../services/case-service'

import { getErrorMessage } from '../utils/error.utils'
export function ConvertCaseToLegalPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [case_, setCase] = useState<Case | null>(null)

  const [formData, setFormData] = useState({
    courtName: '',
    courtNumber: '',
    judgeName: ''
  })

  const loadCase = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      if (!id) throw new Error('ID do caso não encontrado')

      const caseData = await caseService.getCaseById(id)
      setCase(caseData)

      // Pré-preencher se já existir alguma informação
      if (caseData.courtName || caseData.courtNumber || caseData.judgeName) {
        setFormData({
          courtName: caseData.courtName || '',
          courtNumber: caseData.courtNumber || '',
          judgeName: caseData.judgeName || ''
        })
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao carregar caso'))
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    if (id) {
      loadCase()
    }
  }, [id, loadCase])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.courtName && !formData.courtNumber) {
      setError('Informe pelo menos o nome do tribunal ou o número do processo')
      return
    }

    try {
      setSaving(true)
      setError(null)

      const updateData: UpdateCaseDto = {
        courtName: formData.courtName || undefined,
        courtNumber: formData.courtNumber || undefined,
        judgeName: formData.judgeName || undefined,
        tags: [...(case_?.tags || []), 'processo judicial']
      }

      await caseService.updateCase(id!, updateData)
      navigate(`/cases/${id}`)
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao converter caso em processo'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-4 p-4 sm:p-6" aria-busy="true">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-56 max-w-3xl" />
      </div>
    )
  }

  if (error && !case_) {
    return (
      <div className="p-4 sm:p-6">
        <p role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">{error}</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <FormPageHeader
        title="Converter em Processo Judicial"
        description="Adicione informações do tribunal para judicializar este caso"
        icon={Scale}
        backTo={`/cases/${id}`}
        backLabel="Voltar"
      />

      <div className="flex max-w-3xl flex-col gap-4">
        {case_ && (
          <Card size="sm">
            <CardContent>
              <h2 className="font-semibold">Caso: {case_.title}</h2>
              <p className="text-sm text-muted-foreground">#{case_.caseNumber}</p>
              <p className="mt-1 text-sm text-muted-foreground">Cliente: {case_.client?.name}</p>
            </CardContent>
          </Card>
        )}

        <div role="note" className="flex gap-3 rounded-lg border bg-muted/40 p-3">
          <AlertCircle className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <div>
            <h3 className="mb-1 text-sm font-medium">Conversão para Processo Judicial</h3>
            <p className="text-sm text-muted-foreground">
              Ao converter este caso em processo judicial, você estará registrando que o caso
              passou a tramitar no sistema judiciário. Informe os dados do tribunal e do processo.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <FormSection title="Dados do Processo Judicial" icon={Scale}>
            <FormField label="Nome do Tribunal *" htmlFor="convert-court-name" hint="Informe o nome completo do tribunal onde o processo tramita">
              <Input
                id="convert-court-name"
                type="text"
                value={formData.courtName}
                onChange={(e) => setFormData({ ...formData, courtName: e.target.value })}
                placeholder="Ex: Tribunal de Justiça do Estado de São Paulo"
              />
            </FormField>
            <FormField label="Número do Processo *" htmlFor="convert-court-number" hint="Número do processo conforme consta no sistema judiciário">
              <Input
                id="convert-court-number"
                type="text"
                value={formData.courtNumber}
                onChange={(e) => setFormData({ ...formData, courtNumber: e.target.value })}
                placeholder="Ex: 0000000-00.0000.0.00.0000"
              />
            </FormField>
            <FormField label="Nome do Juiz (Opcional)" htmlFor="convert-judge-name" hint="Nome do juiz responsável pelo processo (se conhecido)">
              <Input
                id="convert-judge-name"
                type="text"
                value={formData.judgeName}
                onChange={(e) => setFormData({ ...formData, judgeName: e.target.value })}
                placeholder="Ex: Dr. João da Silva"
              />
            </FormField>
          </FormSection>

          {error && (
            <div role="alert" className="flex gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3">
              <AlertCircle className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden="true" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          <FormActions>
            <Button variant="outline" asChild><Link to={`/cases/${id}`}>Cancelar</Link></Button>
            <Button type="submit" disabled={saving} loading={saving}>
              {saving ? 'Convertendo...' : <><Save className="size-4" /> Converter em Processo Judicial</>}
            </Button>
          </FormActions>
        </form>
      </div>
    </div>
  )
}
